import { Link } from 'react-router-dom';
import { Heart, Sparkles, ArrowRight, Star, Shield, Users, Check } from 'lucide-react';
import { TinyStepsLogo } from '../components/ui/FloatingDecorations';

// ── Dreamy Sakura Palette ──────────────────────────────
const P = {
  blushPink:     '#F68DB3',
  sakuraPink:    '#FFB7CF',
  dustyLavender: '#B88AF8',
  softLilac:     '#D8C2FF',
  warmCream:     '#FFF6F4',
  cottonCandy:   '#FFD6EA',
  lavenderGlow:  '#E9D9FF',
  deepPlum:      '#2E2238',
  softGray:      '#746C7B',
};

const gradientText = {
  background: 'linear-gradient(135deg, #F68DB3, #B88AF8)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const glass = {
  background: 'rgba(255,246,244,0.80)',
  backdropFilter: 'blur(16px) saturate(180%)',
  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.88)',
};

const features = [
  { emoji:'🌸', title:'Period Tracking',   desc:'Smart cycle prediction, ovulation tracking & fertile window.',    cardBg:'rgba(255,214,234,0.58)', borderColor:'rgba(246,141,179,0.30)', iconFrom:'#F68DB3', iconTo:'#D8C2FF' },
  { emoji:'🤱', title:'Pregnancy Journey', desc:'Week-by-week guidance, baby growth visualisation & daily quotes.', cardBg:'rgba(233,217,255,0.58)', borderColor:'rgba(184,138,248,0.30)', iconFrom:'#B88AF8', iconTo:'#D8C2FF' },
  { emoji:'👶', title:'Baby Care',         desc:'Track feeding, sleep, milestones & vaccinations from birth.',     cardBg:'rgba(255,224,238,0.58)', borderColor:'rgba(255,183,207,0.30)', iconFrom:'#FFB7CF', iconTo:'#F68DB3' },
  { emoji:'💝', title:'Memory Vault',      desc:'Scrapbook journal with letters, voice notes & time capsules.',    cardBg:'rgba(255,214,234,0.58)', borderColor:'rgba(246,141,179,0.30)', iconFrom:'#F68DB3', iconTo:'#B88AF8' },
  { emoji:'📋', title:'Medical Records',   desc:'Secure cloud storage for scans, prescriptions & blood reports.',  cardBg:'rgba(233,217,255,0.58)', borderColor:'rgba(184,138,248,0.30)', iconFrom:'#B88AF8', iconTo:'#F68DB3' },
  { emoji:'✨', title:'AI Companion',      desc:'Personalised pregnancy & baby answers, available 24/7.',          cardBg:'rgba(255,224,238,0.58)', borderColor:'rgba(255,183,207,0.30)', iconFrom:'#FFB7CF', iconTo:'#B88AF8' },
];

const testimonials = [
  { name:'Priya S.',  avatar:'👩‍🦱', location:'Mumbai',    verified:true, stars:5, text:'TinySteps felt like a warm hug throughout my pregnancy. The memory vault is pure magic.' },
  { name:'Ananya R.', avatar:'👩‍🦰', location:'Bangalore', verified:true, stars:5, text:'Everything in one beautiful place — from period tracking to baby milestones. I cried happy tears.' },
  { name:'Meera K.',  avatar:'👩',   location:'Delhi',     verified:true, stars:5, text:'The AI answered all my 2am questions with so much care. I felt truly supported.' },
];

const stats = [
  { num:'2M+', label:'Happy Women', emoji:'👩' },
  { num:'50+', label:'Features',      emoji:'✨' },
  { num:'10',  label:'Languages',     emoji:'🌍' },
  { num:'4.9', label:'App Rating',    emoji:'⭐' },
];

const journeyPreviews = [
  { stage:'Period Tracking',    desc:'Understand your body and track your cycle.',
    emoji:'🌸', gradFrom:'#F68DB3', gradTo:'#D8C2FF', borderColor:'rgba(246,141,179,0.38)',
    btnText:'Explore Period Tracking →',
    bullets:['Period Tracking','Cycle Insights','Symptoms Tracking','PCOS Support','Wellness Journal'] },
  { stage:'Trying to Conceive', desc:'Support for your journey to getting pregnant.',
    emoji:'💕', gradFrom:'#B88AF8', gradTo:'#D8C2FF', borderColor:'rgba(184,138,248,0.38)',
    btnText:'Explore TTC Journey →',
    bullets:['Fertility Window','Ovulation Predictions','TTC Tips','TTC Community','TTC AI Companion'] },
  { stage:'Pregnancy Journey',  desc:'Cherish every moment of your tiny miracle.',
    emoji:'🤰', gradFrom:'#B88AF8', gradTo:'#F68DB3', borderColor:'rgba(184,138,248,0.38)',
    btnText:'Explore Pregnancy Journey →',
    bullets:['Week-by-Week Growth','Tiny Miracle','Pregnancy Story','Health Insights','Pregnancy Community'] },
  { stage:'Mom & Baby Journey', desc:'Care, growth, milestones, and memories.',
    emoji:'🍼', gradFrom:'#FFB7CF', gradTo:'#F68DB3', borderColor:'rgba(255,183,207,0.38)',
    btnText:'Explore Mom & Baby Journey →',
    bullets:['Feeding & Sleep','Growth Tracking','Baby Milestones','Baby Memories','Family Moments'] },
];

const BG = { cream:'#FFF6F4', blush:'#FFF0F5', lavender:'#F5EFFF' };
const play = "'Playfair Display',serif";

// Preload hero image immediately when module loads — prevents missing image on route changes
const _preload = new window.Image();
_preload.src = '/hero-illustration1.png';

const lavHearts = [
  { size:17, top:'11%', left:'9%',  delay:'0s',   opacity:0.70 },
  { size:21, top:'7%',  left:'76%', delay:'0.7s', opacity:0.75 },
  { size:14, top:'29%', left:'91%', delay:'1.3s', opacity:0.62 },
  { size:15, top:'55%', left:'9%',  delay:'0.4s', opacity:0.68 },
  { size:19, top:'65%', left:'87%', delay:'1.8s', opacity:0.65 },
  { size:19, top:'70%', left:'11%', delay:'1.1s', opacity:0.58 },
  { size:18, top:'50%', left:'94%', delay:'2.2s', opacity:0.55 },
  { size:19, top:'30%', left:'7%',  delay:'1.6s', opacity:0.60 },
];

const HeroHearts = () => (
  <>
    {lavHearts.map((h, i) => (
      <svg key={i} width={h.size} height={h.size} viewBox="0 0 24 24"
        style={{position:'absolute',top:h.top,left:h.left,opacity:h.opacity,
          animation:`float 4s ease-in-out ${h.delay} infinite`,
          filter:'drop-shadow(0 1px 4px rgba(184,138,248,0.50))',
          pointerEvents:'none'}}
        fill="#B88AF8">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ))}
  </>
);

const LandingPage = () => (
  <div className="min-h-screen overflow-x-hidden" style={{ background:BG.cream, fontFamily:"'Nunito','Inter',sans-serif", color:P.deepPlum }}>

    {/* ── NAVBAR ── */}
    <header className="fixed top-0 left-0 right-0 z-50" style={{...glass, borderBottom:'1px solid rgba(246,141,179,0.22)'}}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <TinyStepsLogo size={36} />
          <div>
            <p className="text-sm font-bold leading-none" style={{fontFamily:play,color:P.deepPlum}}>TinySteps</p>
            <p className="hidden sm:block text-[7px] uppercase tracking-widest leading-none mt-0.5" style={{color:P.softGray}}>A Companion for Every Woman</p>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {['Features','How It Works','Testimonials'].map(l => (
            <a key={l} href="#" className="text-sm font-medium transition-colors" style={{color:P.softGray}}
              onMouseEnter={e=>e.target.style.color=P.blushPink} onMouseLeave={e=>e.target.style.color=P.softGray}>{l}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/login"
            className="whitespace-nowrap text-xs sm:text-sm font-semibold px-4 py-2 sm:px-5 rounded-2xl transition-all border-2"
            style={{color:P.blushPink,borderColor:P.blushPink,background:'transparent'}}
            onMouseEnter={e=>{e.currentTarget.style.background='rgba(246,141,179,0.12)'}}
            onMouseLeave={e=>{e.currentTarget.style.background='transparent'}}>Sign In</Link>
          <Link to="/signup" className="whitespace-nowrap text-xs sm:text-sm font-semibold text-white px-4 py-2 sm:px-5 rounded-2xl transition-all"
            style={{background:'linear-gradient(135deg,#EA86AA,#AF83EC)',boxShadow:'0 4px 14px rgba(246,141,179,0.40)'}}>Get Started</Link>
        </div>
      </div>
    </header>

    {/* ── HERO ── */}
    <section className="relative pt-14 pb-0 overflow-hidden" style={{background:'linear-gradient(160deg,#FFF0F6 0%,#F8F0FF 55%,#EDE4FF 100%)'}}>

      <div className="absolute inset-0 pointer-events-none select-none" style={{zIndex:1,overflow:'hidden'}}>
        <div style={{position:'absolute',top:'-8%',left:'-10%',width:'420px',height:'420px',borderRadius:'50%',
          background:'radial-gradient(circle,rgba(246,141,179,0.18) 0%,rgba(255,183,207,0.08) 45%,transparent 70%)'}}/>
        <div style={{position:'absolute',top:'15%',left:'82%',width:'320px',height:'320px',borderRadius:'50%',
          background:'radial-gradient(circle,rgba(184,138,248,0.15) 0%,rgba(216,194,255,0.06) 50%,transparent 70%)'}}/>
        <div style={{position:'absolute',top:'18%',left:'50%',transform:'translateX(-50%)',width:'600px',height:'340px',borderRadius:'50%',
          background:'radial-gradient(ellipse,rgba(246,141,179,0.12) 0%,rgba(255,183,207,0.06) 50%,transparent 72%)'}}/>
      </div>

      {/* Hero content */}
      <div className="relative pt-10 pb-0 text-center" style={{zIndex:10}}>
        <div className="max-w-7xl mx-auto lg:flex lg:items-center lg:px-12 lg:gap-4">

          {/* LEFT: text + CTA */}
          <div className="px-8 lg:px-0 lg:flex-1 lg:text-left lg:max-w-[560px] lg:pr-6">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest mb-5 animate-slideDown"
              style={{background:'rgba(255,214,234,0.80)',backdropFilter:'blur(12px)',border:'1px solid rgba(246,141,179,0.38)',color:P.blushPink}}>
              <Sparkles className="w-3 h-3" /> A Complete Womanhood Companion
            </div>

            <h1 style={{fontFamily:play,fontWeight:700,lineHeight:1.22,fontSize:'clamp(2rem,3.8vw,3.6rem)',maxWidth:'none',margin:'0 auto 0.25rem',color:P.deepPlum}}>
              Every{' '}<span style={gradientText} className="italic">tiny step</span>{' '}of
              <br/>your journey,{' '}<span className="inline-block animate-sparkle align-middle leading-none" style={{fontSize:'1.3rem'}}>✨</span>
              <br/><span style={gradientText}>lovingly tracked.</span>
            </h1>

            {/* Illustration — mobile only */}
            <div className="lg:hidden" style={{position:'relative',width:'calc(100% + 4rem)',marginLeft:'-2rem',marginRight:'-2rem',marginTop:'-0.5rem',marginBottom:'-2rem'}}>
              <img src="/hero-illustration1.png" alt="Mother lovingly holding her baby"
                loading="eager" fetchPriority="high" decoding="async"
                style={{width:'100%',display:'block',
                  WebkitMaskImage:'radial-gradient(ellipse 86% 82% at 50% 50%, black 32%, transparent 62%)',
                  maskImage:'radial-gradient(ellipse 86% 82% at 50% 50%, black 32%, transparent 62%)'}}/>
              <HeroHearts />
            </div>

            <p className="text-sm lg:text-base leading-relaxed mb-7 max-w-xs lg:max-w-md mx-auto lg:mx-0 mt-1" style={{color:P.softGray}}>
              Track your cycle, understand your health, plan your future, and preserve life's most meaningful moments—all in one place. TinySteps walks beside you with warmth, wisdom, and wonder.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-8 lg:mb-10">
              <Link to="/signup"
                className="flex items-center justify-center gap-2 text-base px-7 py-3 font-bold text-white w-full sm:w-auto active:scale-95 transition-all animate-glow-pulse"
                style={{borderRadius:'9999px',background:'linear-gradient(135deg,#EA86AA 0%,#AF83EC 100%)',boxShadow:'0 4px 22px rgba(246,141,179,0.52),inset 0 1px 0 rgba(255,255,255,0.22)'}}>
                <span>🌸</span> Begin My Journey
              </Link>
              <Link to="/login"
                className="flex items-center justify-center gap-2 text-base px-7 py-3 font-semibold w-full sm:w-auto active:scale-95 transition-all"
                style={{borderRadius:'9999px',background:'rgba(233,217,255,0.20)',border:'1.5px solid rgba(184,138,248,0.42)',color:P.dustyLavender,backdropFilter:'blur(8px)'}}>
                Sign In
              </Link>
            </div>

            {/* Trust badges — desktop */}
            <div className="hidden lg:flex items-center gap-6 text-xs mb-8" style={{color:P.softGray}}>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" style={{color:'#4ade80'}}/> Secure & private</span>
              <span className="flex items-center gap-1.5"><Heart className="w-4 h-4" style={{color:P.blushPink}}/> Made with love</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" style={{color:P.dustyLavender}}/> 2M+ women</span>
            </div>
          </div>

          {/* RIGHT: illustration — desktop only */}
          <div className="hidden lg:block lg:flex-1 relative">
            <div style={{position:'relative',maxWidth:'580px',margin:'0 auto'}}>
              <img src="/hero-illustration1.png" alt="Mother lovingly holding her baby"
                loading="eager" fetchPriority="high" decoding="async"
                style={{width:'100%',display:'block',
                  WebkitMaskImage:'radial-gradient(ellipse 88% 86% at 50% 50%, black 34%, transparent 64%)',
                  maskImage:'radial-gradient(ellipse 88% 86% at 50% 50%, black 34%, transparent 64%)'}}/>
              <HeroHearts />
            </div>
          </div>
        </div>

        {/* Journey preview cards */}
        <div className="max-w-7xl mx-auto px-4 lg:px-12">

          {/* Section header */}
          <div className="text-center mb-5 lg:mb-7">
            <span className="inline-block text-xs font-bold px-4 py-1.5 rounded-full mb-3"
              style={{background:'rgba(255,214,234,0.70)',color:P.blushPink,border:'1px solid rgba(246,141,179,0.32)'}}>
              🌸 Designed for Every Stage of Womanhood
            </span>
            <p className="text-xs lg:text-sm leading-relaxed max-w-xl mx-auto" style={{color:P.softGray}}>
              Whether you're tracking your cycle, trying to conceive, expecting a baby, or caring for your little one, TinySteps adapts to your journey.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 pb-10 items-stretch">
            {journeyPreviews.map((j, i) => (
              <div key={i}
                className="rounded-3xl p-4 lg:p-5 text-left flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg active:scale-[0.98]"
                style={{...glass,border:`1px solid ${j.borderColor}`,boxShadow:'0 4px 20px rgba(246,141,179,0.12)',cursor:'default'}}>

                {/* Icon */}
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl flex items-center justify-center text-lg lg:text-2xl mb-3 flex-shrink-0"
                  style={{background:`linear-gradient(135deg,${j.gradFrom},${j.gradTo})`,boxShadow:'0 3px 10px rgba(246,141,179,0.30)'}}>
                  {j.emoji}
                </div>

                {/* Title + description */}
                <p className="font-bold text-sm mb-1 leading-tight" style={{fontFamily:play,color:P.deepPlum}}>{j.stage}</p>
                <p className="text-[11px] lg:text-xs leading-relaxed mb-3" style={{color:P.softGray}}>{j.desc}</p>

                {/* Feature bullets */}
                <div className="space-y-1.5 flex-1 mb-4">
                  {j.bullets.map((b, bi) => (
                    <div key={bi} className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 shrink-0" style={{color:j.gradFrom}} />
                      <span className="text-[11px] lg:text-xs leading-tight" style={{color:P.softGray}}>{b}</span>
                    </div>
                  ))}
                </div>

                {/* CTA button */}
                <Link to="/signup" className="rounded-full py-2.5 lg:py-3 text-center block transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-95"
                  style={{background:`linear-gradient(135deg,${j.gradFrom},${j.gradTo})`}}>
                  <span className="text-[11px] lg:text-xs font-bold text-white whitespace-nowrap">{j.btnText}</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="relative z-10 border-t" style={{background:'rgba(255,255,255,0.38)',backdropFilter:'blur(8px)',borderColor:'rgba(255,255,255,0.50)'}}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 px-5 py-6 lg:py-10 text-center gap-4">
          {stats.map(s => (
            <div key={s.num}>
              <p className="text-base lg:text-2xl mb-0.5">{s.emoji}</p>
              <p className="text-lg lg:text-3xl font-bold leading-none" style={{fontFamily:play,color:P.deepPlum}}>{s.num}</p>
              <p className="text-[11px] lg:text-sm mt-0.5 leading-tight" style={{color:P.softGray}}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── HOW IT WORKS ── */}
    <section className="py-8 lg:py-16 px-5" style={{background:BG.cream}}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-4 lg:mb-10">
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-3"
            style={{background:'rgba(255,214,234,0.65)',color:P.blushPink,border:'1px solid rgba(246,141,179,0.28)'}}>How It Works</span>
          <h2 className="font-bold text-2xl lg:text-3xl mb-2" style={{fontFamily:play,color:P.deepPlum}}>
            Your journey, <span style={gradientText} className="italic">your way</span>
          </h2>
          <p className="text-sm lg:text-base leading-relaxed" style={{color:P.softGray}}>
            TinySteps adapts to where you are — the app changes completely based on your stage.
          </p>
        </div>
        <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
          {[
            { step:'1', emoji:'📝', title:'Create your account', desc:'Sign up in 30 seconds — just your name, email and a password.' },
            { step:'2', emoji:'🌸', title:'Tell us your stage', desc:'Tracking periods, trying to conceive, pregnant, or caring for a newborn?' },
            { step:'3', emoji:'✨', title:'Get your personalised dashboard', desc:'The app fully adapts — only features relevant to your stage.' },
          ].map((s, i) => (
            <div key={i} className="rounded-3xl p-4 lg:p-6 flex items-start gap-4" style={{...glass,border:'1px solid rgba(255,183,207,0.32)'}}>
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center text-white font-bold text-sm lg:text-lg shrink-0"
                style={{background:'linear-gradient(135deg,#EA86AA,#AF83EC)',boxShadow:'0 3px 12px rgba(246,141,179,0.40)'}}>
                {s.step}
              </div>
              <div>
                <p className="font-semibold text-sm lg:text-base flex items-center gap-2" style={{color:P.deepPlum}}>{s.emoji} {s.title}</p>
                <p className="text-xs lg:text-sm mt-1 leading-relaxed" style={{color:P.softGray}}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 lg:mt-8 text-center">
          <Link to="/signup" className="text-sm lg:text-base py-3 px-8 inline-flex items-center gap-2 text-white font-bold transition-all active:scale-95"
            style={{borderRadius:'9999px',background:'linear-gradient(135deg,#EA86AA,#AF83EC)',boxShadow:'0 4px 18px rgba(246,141,179,0.44)'}}>
            Get Started Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* ── MEMORY VAULT ── */}
    <section className="py-8 lg:py-16 px-5" style={{background:BG.lavender}}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-5 lg:mb-8">
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-2"
            style={{background:'rgba(233,217,255,0.68)',color:P.dustyLavender,border:'1px solid rgba(184,138,248,0.30)'}}>Memory Vault</span>
          <div className="flex justify-center mb-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1 rounded-full"
              style={{background:'linear-gradient(135deg,rgba(233,217,255,0.72),rgba(255,214,234,0.65))',color:'rgba(148,80,185,0.88)',border:'1px solid rgba(184,138,248,0.28)',letterSpacing:'0.01em'}}>
              💜 Available from Pregnancy to Baby's First Year
            </span>
          </div>
          <h2 className="font-bold text-2xl lg:text-3xl mb-2" style={{fontFamily:play,color:P.deepPlum}}>
            A scrapbook that <span style={gradientText} className="italic">writes itself</span>
          </h2>
          <p className="text-sm lg:text-base leading-relaxed mb-2" style={{color:P.softGray}}>Capture and preserve every precious memory from pregnancy through your baby's first birthday—all in one safe place.</p>
          <p className="text-xs lg:text-sm leading-relaxed" style={{color:'rgba(165,120,200,0.72)',fontStyle:'italic'}}>✨ Memory Vault unlocks automatically when you start your Pregnancy Journey and remains available throughout your baby's first year.</p>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4 mb-4 lg:mb-6">
          {[{emoji:'💌',label:'Letters to Baby'},{emoji:'🎵',label:'Voice & Lullabies'},{emoji:'📸',label:'Belly Timeline'},
            {emoji:'🎁',label:'Time Capsules'},{emoji:'📔',label:'Diary Journal'},{emoji:'⭐',label:'Milestones'}].map((m,i) => (
            <div key={i} className="rounded-3xl p-3 lg:p-4 text-center card-hover"
              style={{background:'rgba(255,255,255,0.90)',border:'1px solid rgba(255,183,207,0.30)',boxShadow:'0 2px 12px rgba(246,141,179,0.09)'}}>
              <div className="text-2xl lg:text-3xl mb-1.5">{m.emoji}</div>
              <p className="text-[11px] lg:text-xs font-semibold leading-tight" style={{color:P.deepPlum}}>{m.label}</p>
            </div>
          ))}
        </div>
        <div className="relative overflow-hidden rounded-4xl p-6 lg:p-10 text-white animate-glow-pulse lg:flex lg:items-center lg:gap-10"
          style={{background:'linear-gradient(135deg,#F68DB3,#B88AF8,#D8C2FF)',boxShadow:'0 0 52px rgba(184,138,248,0.48),0 12px 40px rgba(246,141,179,0.38)'}}>
          <div className="absolute inset-0 opacity-10 text-[8rem] flex items-center justify-center select-none pointer-events-none">📖</div>
          <div className="lg:flex-shrink-0 text-center lg:text-left relative z-10">
            <p className="text-4xl lg:text-6xl mb-3 lg:mb-0 animate-bounce-gentle">📖</p>
          </div>
          <div className="relative z-10 text-center lg:text-left">
            <h3 className="font-bold text-xl lg:text-2xl mb-2" style={{fontFamily:play}}>Baby Journey Book</h3>
            <p className="text-sm lg:text-base mb-5 leading-relaxed max-w-xs lg:max-w-none mx-auto lg:mx-0" style={{color:'rgba(255,240,248,0.92)'}}>
              Auto-generate a beautiful digital storybook with your scans, baby photos, voice notes, milestones & love letters.
            </p>
            <Link to="/signup" className="inline-flex items-center gap-2 font-bold px-6 py-2.5 rounded-full transition-colors text-sm lg:text-base"
              style={{background:'rgba(255,255,255,0.96)',color:P.blushPink}}>
              Create My Book <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ── FEATURES ── */}
    <section className="relative py-8 lg:py-16 px-5 overflow-hidden" style={{background:BG.cream}}>
      <div style={{position:'absolute',top:'-10%',right:'-8%',width:'380px',height:'380px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(246,141,179,0.14) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:'-12%',left:'-6%',width:'360px',height:'360px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(184,138,248,0.13) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="max-w-5xl mx-auto relative" style={{zIndex:1}}>
        <div className="text-center mb-6 lg:mb-10">
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-3"
            style={{background:'rgba(233,217,255,0.65)',color:P.dustyLavender,border:'1px solid rgba(184,138,248,0.28)'}}>Everything You Need</span>
          <h2 className="font-bold text-2xl lg:text-3xl mb-3" style={{fontFamily:play,color:P.deepPlum}}>
            One loving home for your <span style={gradientText} className="italic">entire journey</span>
          </h2>
          <p className="text-sm lg:text-base leading-relaxed max-w-xl mx-auto" style={{color:P.softGray}}>
            Health tracking, emotional bonding, memory preservation, and baby care — all in one beautifully designed space.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5 items-stretch">
          {features.map((f, i) => (
            <div key={i} className="rounded-3xl p-4 lg:p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 active:scale-[0.98] cursor-default"
              style={{background:f.cardBg,border:`1px solid ${f.borderColor}`,boxShadow:'0 2px 14px rgba(246,141,179,0.10)'}}>
              <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-2xl flex items-center justify-center text-xl lg:text-2xl mb-2.5 lg:mb-3 flex-shrink-0"
                style={{background:`linear-gradient(135deg,${f.iconFrom},${f.iconTo})`,boxShadow:'0 3px 10px rgba(246,141,179,0.30)'}}>
                {f.emoji}
              </div>
              <h3 className="font-semibold text-sm lg:text-base mb-1" style={{color:P.deepPlum}}>{f.title}</h3>
              <p className="text-xs lg:text-sm leading-relaxed flex-1" style={{color:P.softGray}}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── EMOTIONAL ── */}
    <section className="py-8 lg:py-16 px-5" style={{background:BG.blush}}>
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-5xl lg:text-6xl mb-3 animate-heartbeat">💗</div>
        <h2 className="font-bold text-2xl lg:text-3xl mb-3" style={{fontFamily:play,color:P.deepPlum}}>
          Because <span style={gradientText} className="italic">you deserve</span> to feel celebrated
        </h2>
        <p className="text-sm lg:text-base leading-relaxed max-w-sm lg:max-w-lg mx-auto mb-5 lg:mb-8" style={{color:P.softGray}}>
          TinySteps isn't just an app — it's a warm digital companion that holds your hand through the most precious chapters of your life.
        </p>
        <div className="grid grid-cols-3 gap-3 lg:gap-6">
          {[{emoji:'🌸',label:'Period to\nPregnancy'},{emoji:'🤱',label:'Pregnancy\nJourney'},{emoji:'👶',label:"Baby's\nFirst Year"}].map((s,i) => (
            <div key={i} className="rounded-3xl p-4 lg:p-8 flex flex-col items-center gap-2" style={{...glass,border:'1px solid rgba(255,183,207,0.35)'}}>
              <span className="text-3xl lg:text-5xl animate-bounce-gentle" style={{animationDelay:`${i*0.4}s`}}>{s.emoji}</span>
              <p className="text-xs lg:text-sm font-semibold text-center whitespace-pre-line leading-tight" style={{color:P.deepPlum}}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── TESTIMONIALS ── */}
    <section className="relative py-8 lg:py-16 px-5 overflow-hidden" style={{background:BG.lavender}}>
      <div style={{position:'absolute',top:'-20%',right:'-8%',width:'300px',height:'300px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(184,138,248,0.20) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:'-15%',left:'-6%',width:'280px',height:'280px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(246,141,179,0.14) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="max-w-5xl mx-auto relative" style={{zIndex:1}}>
        <div className="text-center mb-5 lg:mb-10">
          <h2 className="font-bold text-2xl lg:text-3xl" style={{fontFamily:play,color:P.deepPlum}}>Loved by mothers 💗</h2>
          <p className="text-sm lg:text-base mt-1" style={{color:P.softGray}}>Real stories from real mamas</p>
        </div>
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="rounded-3xl p-5 lg:p-6" style={{...glass,border:'1px solid rgba(255,183,207,0.30)',boxShadow:'0 2px 14px rgba(246,141,179,0.09)'}}>
              <div className="flex gap-1 mb-3">
                {[...Array(t.stars)].map((_,j) => <Star key={j} className="w-3.5 h-3.5" style={{color:P.blushPink,fill:P.blushPink}} />)}
              </div>
              <p className="text-sm lg:text-base italic mb-4 leading-loose" style={{color:P.softGray}}>"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{background:'linear-gradient(135deg,rgba(255,214,234,0.82),rgba(233,217,255,0.82))'}}>
                  {t.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-none" style={{color:P.deepPlum}}>{t.name}</p>
                  <p className="text-[11px] mt-0.5" style={{color:P.softGray}}>{t.location}</p>
                </div>
                {t.verified && (
                  <span className="text-[10px] rounded-full px-2 py-0.5 font-semibold flex items-center gap-1 flex-shrink-0"
                    style={{background:'rgba(200,248,225,0.52)',color:'#2e7d52',border:'1px solid rgba(120,200,160,0.42)'}}>
                    ✓ Verified
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── FINAL CTA ── */}
    <section className="relative py-12 lg:py-20 px-5 overflow-hidden" style={{background:'linear-gradient(160deg,#FFF0F6 0%,#F0E8FF 100%)'}}>
      <div style={{position:'absolute',top:'-25%',left:'-8%',width:'380px',height:'380px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(246,141,179,0.14) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:'-20%',right:'-6%',width:'360px',height:'360px',borderRadius:'50%',
        background:'radial-gradient(circle,rgba(184,138,248,0.13) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div className="max-w-lg mx-auto text-center relative" style={{zIndex:1}}>
        <div className="flex justify-center mb-4"><span className="text-4xl lg:text-5xl animate-float-slow">🌸</span></div>
        <h2 className="font-bold text-2xl lg:text-4xl mb-3" style={{fontFamily:play,color:P.deepPlum}}>
          Begin your <span style={gradientText} className="italic">motherhood story</span> today
        </h2>
        <p className="text-sm lg:text-base mb-5 max-w-xs lg:max-w-sm mx-auto" style={{lineHeight:'1.85',color:P.softGray}}>
          Join millions of mothers who trust TinySteps to guide, support, and celebrate their journey.
        </p>
        <p className="text-sm lg:text-base font-semibold mb-4" style={{color:P.blushPink}}>
          💖 Your motherhood memories deserve a home.
        </p>
        <div className="flex flex-col gap-4">
          <Link to="/signup"
            className="flex items-center justify-center gap-2 text-sm lg:text-base font-bold text-white px-8 py-2.5 lg:py-3 mx-auto active:scale-95 transition-all"
            style={{borderRadius:'9999px',background:'linear-gradient(135deg,#EA86AA 0%,#AF83EC 100%)',boxShadow:'0 4px 24px rgba(246,141,179,0.52),inset 0 1px 0 rgba(255,255,255,0.24)'}}>
            <span>🌸</span> Begin My Journey
          </Link>
          <div className="flex items-center justify-center gap-6 text-xs lg:text-sm" style={{color:P.softGray}}>
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" style={{color:'#4ade80'}} /> Secure & private</span>
            <span className="flex items-center gap-1.5"><Heart className="w-4 h-4" style={{color:P.blushPink}} /> Made with love</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" style={{color:P.dustyLavender}} /> 2M+ women</span>
          </div>
        </div>
      </div>
    </section>

    {/* ── FOOTER ── */}
    <footer className="relative py-7 lg:py-12 px-5 overflow-hidden" style={{background:'linear-gradient(180deg,#FFF0F5 0%,#FFF6F4 100%)',borderTop:'1px solid rgba(246,141,179,0.28)'}}>
      <div style={{position:'absolute',top:'-40%',left:'50%',transform:'translateX(-50%)',width:'440px',height:'280px',borderRadius:'50%',
        background:'radial-gradient(ellipse,rgba(246,141,179,0.13) 0%,transparent 68%)',pointerEvents:'none'}}/>
      <div className="max-w-4xl mx-auto text-center relative" style={{zIndex:1}}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <Heart className="w-4 h-4 animate-heartbeat" style={{color:P.blushPink,fill:P.blushPink}} />
          <span className="font-bold text-base lg:text-lg" style={{...gradientText,fontFamily:play}}>TinySteps</span>
        </div>
        <p className="text-xs lg:text-sm mb-4 max-w-xs lg:max-w-sm mx-auto" style={{lineHeight:'1.75',color:P.softGray}}>
          Made with love for every mother's journey — tracking, bonding, and celebrating each tiny step. 💗
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 lg:gap-x-8 gap-y-2 mb-5">
          {['Privacy Policy','Terms of Use','Contact','Support'].map(l => (
            <a key={l} href="#" className="text-xs lg:text-sm font-medium transition-colors" style={{color:P.softGray}}>{l}</a>
          ))}
        </div>
        <div className="flex items-center justify-center gap-3 mb-3">
          <a href="#" title="Instagram" className="w-9 h-9 lg:w-11 lg:h-11 rounded-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
            style={{background:'rgba(255,214,234,0.90)',border:'1px solid rgba(246,141,179,0.28)'}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(246,141,179,0.88)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="rgba(246,141,179,0.88)" stroke="none"/>
            </svg>
          </a>
          <a href="#" title="Email" className="w-9 h-9 lg:w-11 lg:h-11 rounded-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
            style={{background:'rgba(233,217,255,0.90)',border:'1px solid rgba(184,138,248,0.28)'}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(184,138,248,0.88)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </a>
          <a href="#" title="Twitter / X" className="w-9 h-9 lg:w-11 lg:h-11 rounded-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
            style={{background:'rgba(255,214,234,0.90)',border:'1px solid rgba(246,141,179,0.28)'}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(246,141,179,0.88)">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.733-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
        </div>
        <p className="text-[11px] lg:text-xs" style={{color:P.softGray}}>
          © {new Date().getFullYear()} TinySteps · All rights reserved
        </p>
      </div>
    </footer>
  </div>
);

export default LandingPage;
